import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  contentChildren,
  EventEmitter,
  inject,
  Input,
  Output,
  QueryList,
  TemplateRef,
  ViewChildren,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { PageEvent } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { EqualStatusBadgeWidthDirective } from '../../directives';
import { SortableHeaderDirective } from './directive/sortable.directive';
import { TableExpansionDirective } from './directive/table-expansion.directive';
import { TableCellDirective } from './directive/table.cell.directive'; // Assicurati sia importata
import { PaginationResponse } from './models/pagination.model';
import { SortDirection, SortEvent } from './models/sort-event.model';
import { TableColumn } from './models/table-column.model';
import { PaginationComponent } from './pagination/pagination.component';
import { TableService } from './services/table.service';

/**
 * A single row of table data; shape is fully generic, driven by `TableColumn[]`.
 */
export type TableRow = Record<string, unknown>;

@Component({
  selector: 'siat-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.hide-last-column-border]': 'hideLastColumnBorder',
  },
  imports: [
    CommonModule,
    PaginationComponent,
    SortableHeaderDirective,
    MatTableModule,
    MatButtonModule,
    MatCardModule,
    MatTooltip,
    TranslatePipe,
    MatIcon,
    EqualStatusBadgeWidthDirective,
  ],
})
export class TableComponent {
  /**
   * Table columns.
   */
  @Input()
  column: TableColumn[] = [];

  /**
   * Items per page.
   */
  itemsPerPage = [5, 10, 20, 30];

  /**
   * Table rows.
   */
  @Input()
  rows: TableRow[] = [];

  /**
   * Table pagination.
   */
  @Input()
  pagination: PaginationResponse;

  /**
   * Table expandable row tooltip.
   */
  @Input()
  expandableRowTooltip: string;

  /**
   * Hide border on last column.
   */
  @Input()
  hideLastColumnBorder = false;

  /**
   * Predicate marking the rows to show highlighted.
   */
  @Input()
  highlightRow: (row: TableRow) => boolean;

  @Input()
  labelAria: string;

  @Input()
  id: string;

  @Input() widgetId: string;

  /**
   * Table page change event emitter.
   */
  @Output()
  pageChange: EventEmitter<PageEvent> = new EventEmitter<PageEvent>();

  /**
   * Table cell directives query list.
   */
  siatTableCellDirectives = contentChildren(TableCellDirective);

  /**
   * Table expansion directive.
   */
  siatTableExpansionDirective = contentChild(TableExpansionDirective);

  /**
   * Table expansion template.
   */
  expansionTemplate = computed(
    () => this.siatTableExpansionDirective()?.templateRef ?? null
  );

  /**
   * Table expanded element.
   */
  expandedElement: TableRow | null = null;

  /**
   * Custom cell templates.
   */
  customCellTemplates = computed<{ [key: string]: TemplateRef<unknown> }>(() => {
    const templates: { [key: string]: TemplateRef<unknown> } = {};
    for (const dir of this.siatTableCellDirectives()) {
      if (dir.forColumn && dir.templateRef) {
        templates[dir.forColumn] = dir.templateRef;
      }
    }
    return templates;
  });

  /**
   * Table headers query list.
   */
  @ViewChildren(SortableHeaderDirective)
  headers: QueryList<SortableHeaderDirective>;

  /**
   * Streams a boolean telling if the data table is loading or not.
   */
  dataLoading$: Observable<boolean>;

  /**
   * Table click column event emitter.
   */
  @Output() clickColumnEvent = new EventEmitter<{
    sortable: string;
    direction: SortDirection;
  }>();

  /**
   * Table row expansion event emitter.
   */
  @Output() rowExpanded = new EventEmitter<TableRow>();

  tableService = inject(TableService);

  @Input()
  emptyMessage: string;

  @Input()
  title: string;

  @Input()
  actionLabel: string;

  @Input()
  actionDisabled = false;

  @Output()
  onClickAction = new EventEmitter();

  @Input()
  titleClass: string;

  @Input()
  hideHeaderMarginTop = false;

  @Input()
  actionType: 'button' | 'link' = 'button';

  @Input()
  secondaryActionLabel: string;

  @Output()
  onClickSecondaryAction = new EventEmitter();

  @Input()
  customContent: TemplateRef<unknown>;

  constructor() {
    this.dataLoading$ = this.tableService.dataLoading$;
  }

  /**
   * Get nested property value from object using dot notation.
   */
  getNestedValue(obj: TableRow, path: string): unknown {
    return path
      .split('.')
      .reduce(
        (current: unknown, key) =>
          (current as Record<string, unknown> | undefined)?.[key],
        obj as unknown
      );
  }

  /**
   * Toggles the expanded state of a row.
   */
  toggleRow(row: TableRow): void {
    const wasExpanded = this.expandedElement === row;
    this.expandedElement = wasExpanded ? null : row;
    // Emit expansion event when row is expanded (not collapsed)
    if (!wasExpanded) {
      this.rowExpanded.emit(row);
    }
  }

  /**
   * Handle sort event.
   */
  onSort({ sortable, direction }: SortEvent): void {
    for (const header of this.headers) {
      if (header.siatSortable !== sortable) {
        header.direction = 'asc';
      }
    }
    this.clickColumnEvent.emit({ sortable, direction });
  }

  /**
   * Get table columns label.
   */
  getColumns(columns: TableColumn[]): string[] {
    if (!columns) {
      return [];
    }
    const baseColumns = columns.map(c => c.key);

    return this.expansionTemplate() ? ['expand', ...baseColumns] : baseColumns;
  }
}
